import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Divider,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Dashboard,
  Timer,
  Group,
  Assignment,
  BusinessCenter,
  Person,
  PlayArrow,
  BugReport,
} from "@mui/icons-material";

import { DRAWER_WIDTH } from "@/constants";

const navItems = [
  { path: "/dashboard", icon: Dashboard, labelKey: "nav.dashboard" },
  { path: "/tracker", icon: Timer, labelKey: "nav.tracker" },
  { path: "/teams", icon: Group, labelKey: "nav.teams" },
  { path: "/projects", icon: Assignment, labelKey: "nav.projects" },
  { path: "/clients", icon: BusinessCenter, labelKey: "nav.clients" },
  { path: "/account", icon: Person, labelKey: "nav.account" },
];

interface SidebarProps {
  mobileOpen: boolean;
  onClose: () => void;
}

export function Sidebar({ mobileOpen, onClose }: SidebarProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const content = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        py: 2,
      }}
    >
      <Box sx={{ px: 3, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            cursor: "pointer",
          }}
          onClick={() => {
            navigate("/dashboard");
            if (isMobile) onClose();
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Timer sx={{ color: "white", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
              }}
            >
              {t("app.name")}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                color: "text.secondary",
                fontSize: "10px",
                fontWeight: 600,
              }}
            >
              {t("app.subtitle")}
            </Typography>
          </Box>
        </Box>
      </Box>

      <List sx={{ flex: 1, px: 1 }}>
        {navItems.map((item) => {
          const active = location.pathname === item.path;
          return (
            <ListItemButton
              key={item.path}
              onClick={() => {
                navigate(item.path);
                if (isMobile) onClose();
              }}
              sx={{
                borderLeft: 4,
                borderColor: active ? "primary.main" : "transparent",
                bgcolor: active ? "primary.main" + "0d" : "transparent",
                color: active ? "primary.main" : "text.secondary",
                mb: 0.5,
                borderRadius: 0,
                "&:hover": {
                  bgcolor: active ? "primary.main" + "14" : "action.hover",
                },
              }}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
                <item.icon />
              </ListItemIcon>
              <ListItemText
                primary={t(item.labelKey)}
                slotProps={{
                  primary: {
                    sx: {
                      fontSize: 14,
                      fontWeight: 500,
                      fontFamily: "'Manrope', sans-serif",
                    },
                  },
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ px: 2, pb: 1 }}>
        <Button
          variant="contained"
          fullWidth
          startIcon={<PlayArrow />}
          onClick={() => navigate("/tracker")}
          sx={{ mb: 2, py: 1 }}
        >
          {t("tracker.startTimer")}
        </Button>
        <Divider sx={{ mb: 1 }} />
        <List dense>
          <ListItemButton
            component="a"
            href="https://bugs.pinehood.tech/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ borderRadius: 1 }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <BugReport fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t("nav.reportBug")}
              slotProps={{ primary: { sx: { fontSize: 14 } } }}
            />
          </ListItemButton>
        </List>
      </Box>
    </Box>
  );

  return (
    <>
      {isMobile ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={onClose}
          ModalProps={{ keepMounted: true }}
          sx={{
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }}
        >
          {content}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          sx={{
            width: DRAWER_WIDTH,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }}
        >
          {content}
        </Drawer>
      )}
    </>
  );
}
